import { useTranslation } from 'react-i18next'
import { Input, Select } from '@ludus/ui'
import { asTemplate, type ChainPath } from './dataChain.ts'

/**
 * A raw `{{ }}` box with the known paths offered beside it (D5).
 *
 * **Both halves, not either.** The dropdown exists because the events are a
 * closed union, so the console can list what a template may name instead of
 * making a person remember it; the raw box stays because
 * `validateFlowGraph` is what decides whether a template is legal, and a
 * picker-only field would make the drawer a second, stricter opinion — one
 * that would refuse a path a newer engine understands.
 *
 * Picking appends rather than replaces, because a template is usually a
 * sentence with a value in it (`Доставка — {{ $lead.name }}`) rather than a
 * bare path.
 */

interface TemplateInputProps {
  value: string
  paths: ChainPath[]
  onChange: (value: string) => void
}

export function TemplateInput({ value, paths, onChange }: TemplateInputProps) {
  const { t } = useTranslation()

  return (
    <div className="flex flex-col gap-1.5">
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={t('pages.flows.drawer.templateHint', {
          defaultValue: 'Text, or an inserted value',
        })}
      />
      {paths.length > 0 && (
        <Select
          value=""
          onValueChange={(path) => path !== '' && onChange(`${value}${asTemplate(path)}`)}
          placeholder={t('pages.flows.drawer.insertPath', { defaultValue: 'Insert a value…' })}
          options={paths.map((p) => ({
            value: p.path,
            label: p.path,
            hint: t(`pages.flows.drawer.pathGroup.${p.group}`, { defaultValue: p.group }),
          }))}
          triggerClassName="text-xs"
        />
      )}
    </div>
  )
}
